import {
  toWSDateString,
  useIsMobile,
  useModalContext,
  WSCheckboxGroup,
  WSCheckboxToggle,
  WSCircleIconProps,
  WSDateInput,
  WSElement,
  WSElementProps,
  WSFilters,
  WSFlexBox,
  WSForm,
  WSInfiniteScroll,
  WSRadioInputGroup,
  WSSelect,
  WSTable,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ClientWorkFlowStatus,
  ICollaboratorSchema,
  InvoiceStatus,
  IPayableSchema,
  IPayrollSettings,
  IScheduleDate,
  MemberClientStatus,
  MemberWorkFlowStatus
} from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import { addDays } from "date-fns";
import { flatten } from "lodash";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import reduce from "lodash/reduce";
import React, { useCallback, useState } from "react";
import { InfiniteQueryConfig } from "react-query";
import { Route, useHistory, useLocation } from "react-router-dom";
import { useWSInfiniteQuery } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYABLES } from "../../../query/payments/keys";
import {
  InvoicesDateRangeFilter,
  useCollaboratorsQuery,
  usePayrollSettings
} from "../../../query/payments/queries";
import {
  getCollaboratorCompany,
  getCollaboratorName,
  getPayables
} from "../../../query/payments/selectors";
import { WSQueries } from "../../../query/WSQuery";
import {
  InvoicesFilter,
  PayablesWithSummary,
  paymentsService
} from "../../../services/payments";
import { useAuthorizedScopeGroups } from "../../../Settings/utils/teamUtils";
import { isSameDate } from "../../../utils/dates";
import { openInNewTab } from "../../../utils/openInNewTab";
import { useUrlQueryFilters } from "../../../utils/router";
import { CompletedDeductionDetails } from "../../screens/deductions/CompletedDuductionDetails";
import { ScheduledDeductionDetails } from "../../screens/deductions/ScheduledDuductionDetails";
import { PayableDetails } from "../../screens/payables/PayableDetails";
import {
  calculateLineItemsTotal,
  DateRangeFilter,
  getDateRangeFilterRanges
} from "../../utils";
import { getMatchingPayroll, getNextPayrollDate } from "../../utils/payables";
import { BulkApproveModal, PAYABLES_BULK_APPROVE } from "./BulkApproveModal";
import { BulkDeleteModal, PAYABLES_BULK_DELETE } from "./BulkDeleteModal";
import { BulkOpenModal, PAYABLES_BULK_OPEN } from "./BulkOpenModal";
import {
  BulkPreApproveModal,
  PAYABLES_BULK_PREAPPROVE
} from "./BulkPreApproveModal";
import {
  BulkUnapproveModal,
  PAYABLES_BULK_UNAPPROVE
} from "./BulkUnapproveModal";
import styles from "./PayablesTable.module.scss";

export enum PayablesStatusFilter {
  Draft = "Draft",
  ToApprove = "ToApprove",
  Scheduled = "Scheduled",
  Paid = "Paid",
  Overdue = "Overdue"
}

export enum CollaboratorReviewStatusFilter {
  None = "None",
  Accepted = "Accepted",
  Disputed = "Disputed"
}

export interface PayablesFilters {
  status?: PayablesStatusFilter[];

  collaboratorIds?: string[];

  collaboratorReviewStatus?: CollaboratorReviewStatusFilter[];

  openedAtRange?: DateRangeFilter;
  customOpenedAtRange?: Date[];

  paidAtRange?: DateRangeFilter;
  customPaidAtRange?: Date[];

  payrollRunId?: string;

  custom?: InvoicesFilter;
  sort?: {
    updatedAt?: "asc" | "desc";
    "events.paidAt"?: "asc" | "desc";
  };
}

export const usePayablesQuery = (
  filters?: PayablesFilters,
  config?: { size?: number } & InfiniteQueryConfig<
    PayablesWithSummary,
    AxiosError
  >
) => {
  const { hasPaymentsScope } = useAuthorizedScopeGroups();

  const size = config?.size || 100;

  const query = useWSInfiniteQuery<PayablesWithSummary, AxiosError>(
    [QUERY_PAYABLES, { filters, size }],
    (_, __, pageNumber = 1) => {
      let filter: InvoicesFilter = {
        "labels.invoiceType": {
          "!=": "approvedInvoicesPayment"
        }
      };

      // Status

      const allowedStatuses = new Set<InvoiceStatus>();

      if (filters?.status && filters.status.length !== 5) {
        filters.status.forEach(status => {
          switch (status) {
            case PayablesStatusFilter.Draft:
              allowedStatuses.add(InvoiceStatus.Draft);
              break;
            case PayablesStatusFilter.ToApprove:
            case PayablesStatusFilter.Scheduled:
              allowedStatuses.add(InvoiceStatus.Open);
              allowedStatuses.add(InvoiceStatus.Overdue);
              allowedStatuses.add(InvoiceStatus.Pending);
              break;
            case PayablesStatusFilter.Paid:
              allowedStatuses.add(InvoiceStatus.Paid);
              allowedStatuses.add(InvoiceStatus.PaymentInTransit);
              break;
            case PayablesStatusFilter.Overdue:
              allowedStatuses.add(InvoiceStatus.Overdue);
              break;
            default:
          }
        });

        filter.status = {
          in: Array.from(allowedStatuses)
        };

        if (
          filters.status.includes(PayablesStatusFilter.Scheduled) &&
          !filters.status.includes(PayablesStatusFilter.ToApprove)
        ) {
          filter["client.workflowStatus"] = ClientWorkFlowStatus.Approved;
        } else if (
          filters.status.includes(PayablesStatusFilter.ToApprove) &&
          !filters.status.includes(PayablesStatusFilter.Scheduled)
        ) {
          filter["client.workflowStatus"] = {
            notIn: [
              ClientWorkFlowStatus.Approved,
              ClientWorkFlowStatus.PaymentInitiated,
              ClientWorkFlowStatus.Funded
            ]
          };
        }

        // If it's only to approve – hide PPL invoices
        if (
          filters.status.length === 1 &&
          filters.status[0] === PayablesStatusFilter.ToApprove
        ) {
          filter["labels.creationSource"] = {
            "!=": "personalPaylink"
          };
        }
      } else {
        filter.status = {
          "!=": InvoiceStatus.Cancelled
        };
      }

      // Collaborator review status

      if (filters?.collaboratorReviewStatus) {
        // Only None
        if (
          filters.collaboratorReviewStatus.length === 1 &&
          filters.collaboratorReviewStatus[0] ===
            CollaboratorReviewStatusFilter.None
        ) {
          filter["member.workflowStatus"] = {
            notIn: [
              MemberWorkFlowStatus.Accepted,
              MemberWorkFlowStatus.Disputed
            ]
          };
        }

        // Only Accepted
        if (
          filters.collaboratorReviewStatus.length === 1 &&
          filters.collaboratorReviewStatus[0] ===
            CollaboratorReviewStatusFilter.Accepted
        ) {
          filter["member.workflowStatus"] = MemberWorkFlowStatus.Accepted;
        }

        // Only Disputed
        if (
          filters.collaboratorReviewStatus.length === 1 &&
          filters.collaboratorReviewStatus[0] ===
            CollaboratorReviewStatusFilter.Disputed
        ) {
          filter["member.workflowStatus"] = MemberWorkFlowStatus.Disputed;
        }

        // Accepted and Disputed
        if (
          filters.collaboratorReviewStatus.length === 2 &&
          !filters.collaboratorReviewStatus.includes(
            CollaboratorReviewStatusFilter.None
          )
        ) {
          filter["member.workflowStatus"] = {
            in: [MemberWorkFlowStatus.Accepted, MemberWorkFlowStatus.Disputed]
          };
        }

        // None and Disputed
        if (
          filters.collaboratorReviewStatus.length === 2 &&
          !filters.collaboratorReviewStatus.includes(
            CollaboratorReviewStatusFilter.Accepted
          )
        ) {
          filter["member.workflowStatus"] = {
            "!=": MemberWorkFlowStatus.Accepted
          };
        }

        // None and Accepted
        if (
          filters.collaboratorReviewStatus.length === 2 &&
          !filters.collaboratorReviewStatus.includes(
            CollaboratorReviewStatusFilter.Disputed
          )
        ) {
          filter["member.workflowStatus"] = {
            "!=": MemberWorkFlowStatus.Disputed
          };
        }
      }

      // Collaborator ID
      if (filters?.collaboratorIds && filters.collaboratorIds.length > 0) {
        filter.memberClientId = {
          in: filters.collaboratorIds
        };
      }

      // Opened at

      const openedAtRange = getDateRangeFilterRanges(
        filters?.openedAtRange || DateRangeFilter.All,
        filters?.customOpenedAtRange
      );

      if (openedAtRange) {
        filter["events.openedAt"] = {
          ">": openedAtRange[0],
          "<": openedAtRange[1]
        };
      }

      // Paid at

      const paidAtRange = getDateRangeFilterRanges(
        filters?.paidAtRange || DateRangeFilter.All,
        filters?.customPaidAtRange
      );

      if (paidAtRange) {
        filter["events.paidAt"] = {
          ">": paidAtRange[0],
          "<": paidAtRange[1]
        };
      }

      // Payroll run id

      if (filters?.payrollRunId) {
        filter["internal.collaborators.parentInvoiceId"] = filters.payrollRunId;
      }

      // Custom

      if (filters?.custom) {
        filter = {
          ...filter,
          ...filters.custom
        };
      }

      return paymentsService.payable.list({
        filter,
        page: {
          size,
          number: pageNumber
        },
        sort: filters?.sort
          ? filters.sort
          : {
              updatedAt: "desc"
            }
      });
    },
    {
      getFetchMore: (lastPage, allPages) => {
        if (lastPage.data.length < size) {
          return undefined;
        } else {
          return allPages.length + 1;
        }
      },
      enabled: hasPaymentsScope,
      ...config
    }
  );

  return {
    ...query,
    data: query.data
      ? {
          data: flatten(query.data.map(page => page.data)),
          summary: query.data[0]?.summary
        }
      : undefined
  };
};

type PayablesTableItem = WSTableItem<PayablesTableItemData>;

export type PayablesTableItemData = {
  collaborator?: ICollaboratorSchema;
  payable?: IPayableSchema;
  payrollRun?: IScheduleDate;
};

type PayablesTableProps = {
  bulkActions?: ("open" | "preapprove" | "approve" | "unapprove" | "delete")[];
  visibleFilters?: {
    status?: boolean;
    collaborator?: boolean;
    collaboratorReviewStatus?: boolean;
    openedAtRange?: boolean;
    paidAtRange?: boolean;
  };
  defaultFilters?: PayablesFilters;
  pagination?: {
    limit?: number;
    disableInfiniteScroll?: boolean;
  };
  showInvoiceNumber?: boolean;
  showDateRequested?: boolean;
  showPayDate?: boolean;
  showDatePaid?: boolean;
  showHeaders?: boolean;
  showLatestUpdate?: boolean;
  showPayrollGroups?: boolean;
  hideSelectAll?: boolean;
  basePath: string;
  emptyState?: EmptyStateProps;
} & WSElementProps;

export const PayablesTable: React.FC<PayablesTableProps> = ({
  bulkActions,
  defaultFilters,
  showInvoiceNumber,
  showDateRequested,
  showPayDate,
  showDatePaid,
  showHeaders,
  showLatestUpdate,
  showPayrollGroups,
  hideSelectAll,
  basePath,
  pagination,
  visibleFilters,
  emptyState,
  ...elementProps
}) => {
  const userId = useUserId();
  const history = useHistory();
  const location = useLocation();
  const collaboratorsQuery = useCollaboratorsQuery();
  const payrollSettingsQuery = usePayrollSettings(userId);
  const { openModal } = useModalContext();
  const isMobile = useIsMobile();
  const defaultFiltersPopulated: PayablesFilters = {
    status: [
      PayablesStatusFilter.Draft,
      PayablesStatusFilter.ToApprove,
      PayablesStatusFilter.Scheduled,
      PayablesStatusFilter.Paid,
      PayablesStatusFilter.Overdue
    ],
    collaboratorIds: [],
    collaboratorReviewStatus: [
      CollaboratorReviewStatusFilter.None,
      CollaboratorReviewStatusFilter.Accepted,
      CollaboratorReviewStatusFilter.Disputed
    ],
    openedAtRange: DateRangeFilter.All,
    paidAtRange: DateRangeFilter.All,
    ...defaultFilters
  };
  const { filters, setFilters } = useUrlQueryFilters<PayablesFilters>(
    defaultFiltersPopulated
  );
  const payablesQuery = usePayablesQuery(filters, {
    size: pagination?.limit
  });

  const [selectedPayableIds, setSelectedPayableIds] = useState<string[]>([]);
  const selectPayable = (invoiceId: string) => {
    const index = selectedPayableIds.indexOf(invoiceId);
    let newSelectedPayableIds = [...selectedPayableIds];
    index === -1
      ? newSelectedPayableIds.push(invoiceId)
      : newSelectedPayableIds.splice(index, 1);
    setSelectedPayableIds(newSelectedPayableIds);
  };

  const onView = useCallback(
    (item: PayablesTableItem) => {
      if (item.data.payable) {
        history.push({
          pathname: `${basePath}/${item.id}`,
          search: location.search,
          state: {
            backPath: basePath
          }
        });
      }
    },
    [location.search]
  );

  const onViewInNewTab = useCallback(
    (item: PayablesTableItem) => {
      if (item.data.payable) {
        openInNewTab(`${basePath}/${item.id}${location.search}`);
      }
    },
    [location.search]
  );

  return (
    <WSElement {...elementProps}>
      <Route
        path={`${basePath}/:payableId/deduction/scheduled/:deductionId`}
        component={ScheduledDeductionDetails}
      />
      <Route
        path={`${basePath}/:payableId/deduction/completed/:deductionId`}
        component={CompletedDeductionDetails}
      />
      <Route path={`${basePath}/:payableId`} component={PayableDetails} exact />
      <WSQueries
        queries={{ collaboratorsQuery, payablesQuery, payrollSettingsQuery }}
      >
        {({
          collaboratorsQuery: { data: collaborators },
          payablesQuery: {
            data: { data: _payables }
          },
          payrollSettingsQuery: { data: payrollSettings }
        }) => {
          const payables = getPayables(_payables);

          let data: PayablesTableItem[] = [
            ...payables.map(payable => ({
              id: payable.payableId,
              mark:
                (payable.status === InvoiceStatus.Pending &&
                  payable.client.workflowStatus ===
                    ClientWorkFlowStatus.Approved) ||
                ((payable.status === InvoiceStatus.Open ||
                  payable.status === InvoiceStatus.Overdue ||
                  payable.status === InvoiceStatus.Pending) &&
                  payable.member.workflowStatus ===
                    MemberWorkFlowStatus.Disputed)
                  ? ("yellow500" as any)
                  : undefined,
              data: {
                payable,
                ...(showPayrollGroups || payrollSettings.scheduleDates
                  ? {
                      payrollRun: getMatchingPayroll(payrollSettings, payable)
                    }
                  : {}),
                collaborator: collaborators.find(
                  collaborator =>
                    collaborator.collaboratorId === payable.collaboratorId
                )
              },
              icon: buildPayableIcon(payable),
              selected: selectedPayableIds.includes(payable.payableId)
            }))
          ];

          if (showPayrollGroups) {
            // sort by payroll run date
            data = data.sort((a, b) => {
              if (a.data.payrollRun && b.data.payrollRun) {
                return a.data.payrollRun.date > b.data.payrollRun.date ? 1 : -1;
              } else if (a.data.payrollRun) {
                return 1;
              } else if (b.data.payrollRun) {
                return -1;
              } else {
                return 0;
              }
            });
          }

          const getElementsDataByIds = (ids: string[]) =>
            data.filter(item => ids.includes(item.id)).map(item => item.data);

          const getUniqueCollaborators = (
            collaborators: ICollaboratorSchema[]
          ): ICollaboratorSchema[] => {
            return [
              ...new Map(
                collaborators.map(collaborator => [
                  collaborator.memberId,
                  collaborator
                ])
              ).values()
            ];
          };

          const getAssociatedCollaboratorsForSelectedCollaborator = (
            selectedCollaboratorId: string
          ): ICollaboratorSchema[] => {
            const selectedCollaborator = collaborators.find(
              collaborator =>
                collaborator.collaboratorId === selectedCollaboratorId
            );
            return collaborators.filter(
              collaborator =>
                collaborator.memberId === selectedCollaborator?.memberId
            );
          };

          const uniqueCollaborators = getUniqueCollaborators(collaborators);
          const filteredCollaborators = uniqueCollaborators.filter(
            collaborator => collaborator.status !== MemberClientStatus.Inactive
          );

          return (
            <>
              {visibleFilters && (
                <WSFilters
                  mb="M"
                  filters={filters}
                  defaultFilters={defaultFiltersPopulated}
                  getCount={(defaultValues, currentValues) => {
                    const _defaultValues = omit(defaultValues, ["sort"]);
                    const _currentValues = omit(currentValues, ["sort"]);

                    return reduce<any, any>(
                      _currentValues,
                      (result, value, key) =>
                        isEqual(value, _defaultValues[key])
                          ? result
                          : result.concat(key),
                      []
                    ).length;
                  }}
                  onFilter={newFilters => {
                    const associatedCollaborators = getAssociatedCollaboratorsForSelectedCollaborator(
                      newFilters.collaboratorIds
                    );
                    const updatedFilters = {
                      ...filters,
                      ...newFilters,
                      collaboratorIds: associatedCollaborators.map(
                        collaborator => collaborator.collaboratorId
                      )
                    };

                    setFilters(updatedFilters);

                    payablesQuery.refetch();
                  }}
                >
                  {visibleFilters.status && (
                    <WSForm.Field
                      name="status"
                      label="Status"
                      mb="L"
                      component={WSCheckboxGroup}
                      componentProps={{
                        options: Object.keys(PayablesStatusFilter).map(key => ({
                          label: key,
                          value: key
                        }))
                      }}
                    />
                  )}
                  {visibleFilters.collaborator && (
                    <WSForm.Field
                      name="collaboratorIds"
                      label="Collaborator"
                      mb="L"
                      component={WSSelect}
                      componentProps={{
                        searchable: true,
                        placeholder: "All collaborators",
                        options: filteredCollaborators.map(collaborator => ({
                          label: getCollaboratorName(collaborator),
                          value: collaborator.collaboratorId,
                          searchText: `${collaborator.member.user.email} ${collaborator.member.user.profile?.firstName} ${collaborator.member.user.profile?.lastName} ${collaborator.member.profile?.company?.name}`
                        }))
                      }}
                    />
                  )}

                  {visibleFilters.collaboratorReviewStatus && (
                    <WSForm.Field
                      name="collaboratorReviewStatus"
                      label="Collaborator review status"
                      mb="L"
                      component={WSCheckboxGroup}
                      componentProps={{
                        options: Object.keys(
                          CollaboratorReviewStatusFilter
                        ).map(key => ({
                          label: key,
                          value: key
                        }))
                      }}
                    />
                  )}

                  {visibleFilters.openedAtRange && (
                    <WSForm.Field
                      name="openedAtRange"
                      label="Requested date range"
                      mb="L"
                      component={WSRadioInputGroup}
                      componentProps={{
                        options: [
                          {
                            label: "All time",
                            value: InvoicesDateRangeFilter.All
                          },
                          {
                            label: "Past week",
                            value: InvoicesDateRangeFilter.PastWeek
                          },
                          {
                            label: "Past month",
                            value: InvoicesDateRangeFilter.PastMonth
                          },
                          {
                            label: "Past year",
                            value: InvoicesDateRangeFilter.PastYear
                          },
                          {
                            label: (
                              <WSForm.Context>
                                {({ setValue }) => (
                                  <WSFlexBox
                                    alignItems="center"
                                    className={styles.dateRange}
                                  >
                                    <WSText mr="S">From</WSText>
                                    <WSForm.Field
                                      name="customOpenedAtRange"
                                      component={WSDateInput}
                                      componentProps={{
                                        range: true,
                                        placeholder: "Select date range",
                                        onFocus() {
                                          setValue(
                                            "openedAtRange",
                                            InvoicesDateRangeFilter.Custom
                                          );
                                        }
                                      }}
                                    />
                                  </WSFlexBox>
                                )}
                              </WSForm.Context>
                            ),
                            value: InvoicesDateRangeFilter.Custom
                          }
                        ]
                      }}
                    />
                  )}

                  {visibleFilters.paidAtRange && (
                    <WSForm.Field
                      name="paidAtRange"
                      label="Paid date range"
                      mb="L"
                      component={WSRadioInputGroup}
                      componentProps={{
                        options: [
                          {
                            label: "All time",
                            value: InvoicesDateRangeFilter.All
                          },
                          {
                            label: "Past week",
                            value: InvoicesDateRangeFilter.PastWeek
                          },
                          {
                            label: "Past month",
                            value: InvoicesDateRangeFilter.PastMonth
                          },
                          {
                            label: "Past year",
                            value: InvoicesDateRangeFilter.PastYear
                          },
                          {
                            label: (
                              <WSForm.Context>
                                {({ setValue }) => (
                                  <WSFlexBox
                                    alignItems="center"
                                    className={styles.dateRange}
                                  >
                                    <WSText mr="S">From</WSText>
                                    <WSForm.Field
                                      name="customPaidAtRange"
                                      component={WSDateInput}
                                      componentProps={{
                                        range: true,
                                        placeholder: "Select date range",
                                        onFocus() {
                                          setValue(
                                            "paidAtRange",
                                            InvoicesDateRangeFilter.Custom
                                          );
                                        }
                                      }}
                                    />
                                  </WSFlexBox>
                                )}
                              </WSForm.Context>
                            ),
                            value: InvoicesDateRangeFilter.Custom
                          }
                        ]
                      }}
                    />
                  )}
                </WSFilters>
              )}

              {bulkActions && bulkActions.length > 0 && data.length > 0 && (
                <>
                  <WSTable.SelectHelper
                    mb="M"
                    items={selectedPayableIds}
                    onClearItems={() => setSelectedPayableIds([])}
                    actions={bulkActions.map(action => {
                      switch (action) {
                        case "open":
                          return {
                            label: "Open",
                            onClick: () => {
                              openModal(PAYABLES_BULK_OPEN, {
                                data: getElementsDataByIds(selectedPayableIds),
                                onFinish: () => {
                                  setSelectedPayableIds([]);
                                }
                              });
                            }
                          };
                        case "preapprove":
                          return {
                            label: "Pre-approve",
                            onClick: () => {
                              openModal(PAYABLES_BULK_PREAPPROVE, {
                                data: getElementsDataByIds(selectedPayableIds),
                                onFinish: () => {
                                  setSelectedPayableIds([]);
                                }
                              });
                            }
                          };
                        case "approve":
                          return {
                            label: "Approve for payment",
                            onClick: () => {
                              openModal(PAYABLES_BULK_APPROVE, {
                                data: getElementsDataByIds(selectedPayableIds),
                                onFinish: () => {
                                  setSelectedPayableIds([]);
                                }
                              });
                            }
                          };
                        case "unapprove":
                          return {
                            label: "Unapprove",
                            onClick: () => {
                              openModal(PAYABLES_BULK_UNAPPROVE, {
                                data: getElementsDataByIds(selectedPayableIds),
                                onFinish: () => {
                                  setSelectedPayableIds([]);
                                }
                              });
                            }
                          };
                        case "delete":
                        default:
                          return {
                            label: "Delete",
                            onClick: () => {
                              openModal(PAYABLES_BULK_DELETE, {
                                data: getElementsDataByIds(selectedPayableIds),
                                onFinish: () => {
                                  setSelectedPayableIds([]);
                                }
                              });
                            }
                          };
                      }
                    })}
                  />
                  <BulkOpenModal />
                  <BulkPreApproveModal />
                  <BulkApproveModal />
                  <BulkUnapproveModal />
                  <BulkDeleteModal />

                  {!hideSelectAll && (
                    <WSCheckboxToggle
                      mb="M"
                      label={
                        <WSText.Heading3>Select all payables</WSText.Heading3>
                      }
                      name="selectAll"
                      value={selectedPayableIds.length === data.length}
                      onChange={value => {
                        if (value) {
                          setSelectedPayableIds(data.map(item => item.id));
                        } else {
                          setSelectedPayableIds([]);
                        }
                      }}
                    />
                  )}
                </>
              )}

              <WSInfiniteScroll
                onLoad={() => {
                  payablesQuery.fetchMore();
                }}
                loadMore={
                  !pagination?.disableInfiniteScroll && payables.length > 0
                }
                endOfList={
                  !payablesQuery.canFetchMore ||
                  pagination?.disableInfiniteScroll
                }
                loading={!!payablesQuery.isFetchingMore}
              >
                <WSTable<PayablesTableItemData>
                  showIcon
                  tableData={data}
                  getGroupLabel={
                    showPayrollGroups
                      ? item => {
                          if (item.data.payrollRun) {
                            return `${toWSDateString(
                              item.data.payrollRun.date,
                              "monthDate"
                            )} Check Date`;
                          }
                          return `Not upcoming payroll`;
                        }
                      : undefined
                  }
                  columns={[
                    {
                      config: {
                        gridTemplateSizeMax: "2fr",
                        header: showHeaders
                          ? {
                              text: "Name"
                            }
                          : undefined
                      },
                      renderFunction: ({ data: { collaborator } }) =>
                        collaborator && (
                          <>
                            <WSText mb="XS">
                              {getCollaboratorName(collaborator)}
                            </WSText>
                            <WSText.Heading5>
                              {getCollaboratorCompany(collaborator)}
                            </WSText.Heading5>
                          </>
                        )
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "0.5fr",
                        justify: "start",
                        hideOnScreens: showInvoiceNumber
                          ? ["XS", "S"]
                          : ["XS", "S", "M", "L", "XL"],
                        header: showHeaders
                          ? {
                              text: "Invoice number"
                            }
                          : undefined
                      },
                      renderFunction: ({ data: { payable } }) => (
                        <WSText color="grey700">
                          {payable?.invoiceNumber}
                        </WSText>
                      )
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "0.5fr",
                        justify: "start",
                        hideOnScreens: showDateRequested
                          ? ["XS", "S"]
                          : ["XS", "S", "M", "L", "XL"],
                        header: showHeaders
                          ? {
                              text: "Date requested"
                            }
                          : undefined
                      },
                      renderFunction: ({ data: { payable } }) =>
                        payable?.status === InvoiceStatus.Pending ? (
                          <WSText.Heading5
                            color={
                              payable.client.workflowStatus ===
                              ClientWorkFlowStatus.Approved
                                ? "yellow500"
                                : "grey700"
                            }
                          >
                            Eligibility pending
                          </WSText.Heading5>
                        ) : (
                          <WSText formatDate="monthDayYear" color="grey700">
                            {payable?.events.openedAt}
                          </WSText>
                        )
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "0.5fr",
                        justify: "start",
                        hideOnScreens: showPayDate
                          ? ["XS", "S"]
                          : ["XS", "S", "M", "L", "XL"],
                        header: showHeaders
                          ? {
                              text: "Pay date"
                            }
                          : undefined
                      },
                      renderFunction: ({ data: { payable } }) => (
                        <WSText formatDate="monthDayYear" color="grey700">
                          {payable?.client.payDate || payable?.dueDate}
                        </WSText>
                      )
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "0.5fr",
                        justify: "start",
                        hideOnScreens: showDatePaid
                          ? ["XS", "S"]
                          : ["XS", "S", "M", "L", "XL"],
                        header: showHeaders
                          ? {
                              text: "Date paid"
                            }
                          : undefined,
                        sortDirection:
                          filters.sort?.["events.paidAt"] === "asc"
                            ? "ascending"
                            : "descending",
                        onColumnSort(direction) {
                          return setFilters({
                            sort: {
                              "events.paidAt":
                                direction === "ascending" ? "asc" : "desc"
                            }
                          });
                        }
                      },
                      renderFunction: ({ data: { payable } }) => (
                        <WSText formatDate="monthDayYear" color="grey700">
                          {payable?.events.paidAt}
                        </WSText>
                      )
                    },
                    {
                      config: {
                        gridTemplateSizeMax: showLatestUpdate
                          ? "0.6fr"
                          : "0.5fr",
                        justify: "end",
                        header: showHeaders
                          ? {
                              text: "Amount"
                            }
                          : undefined
                      },
                      renderFunction: ({ data: { payable } }) => (
                        <WSFlexBox
                          direction="column"
                          alignItems="flex-end"
                          wrap="nowrap"
                        >
                          <WSText
                            mb={
                              showLatestUpdate
                                ? isMobile
                                  ? undefined
                                  : "XS"
                                : undefined
                            }
                            formatMoney
                            color="grey700"
                          >
                            {payable
                              ? calculateLineItemsTotal(payable.lineItems) +
                                (payable.chargedFees?.lateFee?.amount || 0)
                              : null}
                          </WSText>
                          {showLatestUpdate && payable
                            ? renderInvoiceLatestUpdate(
                                payable,
                                payrollSettings
                              )
                            : null}
                        </WSFlexBox>
                      )
                    }
                  ]}
                  onRowClick={onView}
                  onRowControlClick={onViewInNewTab}
                  onRowMousewheelClick={onViewInNewTab}
                  onRowSelect={
                    bulkActions && bulkActions.length > 0
                      ? ({ data: { payable } }) => {
                          selectPayable(payable?.payableId || "");
                        }
                      : undefined
                  }
                  onMultiRowSelect={items => {
                    setSelectedPayableIds(prev => [
                      ...prev.filter(id => !items.some(i => i.id === id)),
                      ...(items.some(i => !i.selected)
                        ? items.map(i => i.id)
                        : [])
                    ]);
                  }}
                  data-testid="payablesTable"
                />
                {data.length === 0 && emptyState && (
                  <PayablesEmptyState {...emptyState} />
                )}
              </WSInfiniteScroll>
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};

export const buildPayableIcon = (payable: IPayableSchema): WSCircleIconProps =>
  payable.status === InvoiceStatus.Draft
    ? {
        icon: "pencil",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : payable.client.workflowStatus === ClientWorkFlowStatus.Approved &&
      (payable.status === InvoiceStatus.Open ||
        payable.status === InvoiceStatus.Overdue)
    ? {
        icon: "calendar",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : payable.status === InvoiceStatus.Open
    ? {
        icon: "mail",
        color: "blue100",
        secondaryColor: "blue500"
      }
    : payable.status === InvoiceStatus.Overdue
    ? {
        icon: "mail",
        color: "blue100",
        secondaryColor: "blue500",
        badge: {
          text: "!",
          color: "red500",
          secondaryColor: "white"
        }
      }
    : payable.status === InvoiceStatus.PaymentInTransit
    ? {
        icon: "checkLine",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : payable.status === InvoiceStatus.Paid
    ? {
        icon: "checkLine",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : payable.status === InvoiceStatus.Pending
    ? {
        icon: "clock",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : {
        icon: "pencil",
        color: "grey200",
        secondaryColor: "grey600"
      };

const renderInvoiceLatestUpdate = (
  payable: IPayableSchema,
  payrollSettings: IPayrollSettings
) =>
  payable.status === InvoiceStatus.Draft ? (
    isSameDate(payable.createdAt, payable.updatedAt) ? (
      <WSText.Heading5 color="grey600" align="right">
        Created {toWSDateString(payable.createdAt, "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Last updated {toWSDateString(payable.updatedAt, "monthDate")}
      </WSText.Heading5>
    )
  ) : payable.status === InvoiceStatus.Open ? (
    addDays(new Date(), 3) > payable.dueDate ? (
      <WSText.Heading5 color="yellow500" align="right">
        Due soon ({toWSDateString(payable.dueDate, "monthDate")})
      </WSText.Heading5>
    ) : payable.client.workflowStatus === ClientWorkFlowStatus.Approved ? (
      <WSText.Heading5 color="grey600" align="right">
        Approved to be paid on{" "}
        {toWSDateString(getNextPayrollDate(payrollSettings), "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Due ({toWSDateString(payable.dueDate, "monthDate")})
      </WSText.Heading5>
    )
  ) : payable.status === InvoiceStatus.Overdue ? (
    payable.client.workflowStatus === ClientWorkFlowStatus.Approved ? (
      <WSText.Heading5 color="grey600" align="right">
        Approved to be paid on{" "}
        {toWSDateString(getNextPayrollDate(payrollSettings), "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="red500" align="right">
        Overdue ({toWSDateString(payable.dueDate, "monthDate")})
      </WSText.Heading5>
    )
  ) : payable.status === InvoiceStatus.Paid ? (
    <WSText.Heading5 color="grey600" align="right">
      Paid {toWSDateString(payable.events.paidAt, "monthDate")}
    </WSText.Heading5>
  ) : payable.status === InvoiceStatus.PaymentInTransit ? (
    <WSText.Heading5 color="grey600" align="right">
      Payment in route
    </WSText.Heading5>
  ) : payable.status === InvoiceStatus.Pending ? (
    payable.client.workflowStatus === ClientWorkFlowStatus.PaymentInitiated ||
    payable.client.workflowStatus === ClientWorkFlowStatus.Funded ? (
      <WSText.Heading5 color="grey600" align="right">
        Funded for payment
      </WSText.Heading5>
    ) : payable.client.workflowStatus === ClientWorkFlowStatus.Approved ? (
      <WSText.Heading5 color="grey600" align="right">
        Approved to be funded on{" "}
        {toWSDateString(getNextPayrollDate(payrollSettings), "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="yellow500" align="right">
        Eligibility pending
      </WSText.Heading5>
    )
  ) : null;

type EmptyStateProps = {
  firstTime?: string;
  standart: string;
};

export const PayablesEmptyState: React.FC<EmptyStateProps> = ({
  firstTime,
  standart
}) => {
  const payablesQuery = usePayablesQuery({}, { size: 1 });

  return (
    <WSQueries queries={{ payablesQuery }}>
      {({
        payablesQuery: {
          data: { data: payables }
        }
      }) => {
        return (
          <WSFlexBox justify="center" mt="M">
            <WSText color="grey700">
              {payables.length === 0 && firstTime ? firstTime : standart}
            </WSText>
          </WSFlexBox>
        );
      }}
    </WSQueries>
  );
};
